import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  User,
  Users,
  Mail,
  ShieldCheck,
  Bell,
  MessageSquare,
  HelpCircle,
  Shield,
  FileText,
  ChevronRight,
  Forward,
  ArrowLeftRight,
  Plus,
} from 'lucide-react';

import { HamburgerMenuButton } from '@/components/Layout/mobile/HamburgerMenuButton';
import { FullScreenSheet, ListItem } from '@/components/ui';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { AhaWalkthroughModal } from '@/components/domain/shared/AhaWalkthrough';
import { PrivacyPolicyBody } from './legal/PrivacyPolicyBody';
import { TermsOfServiceBody } from './legal/TermsOfServiceBody';
import { FaqBody } from './legal/FaqBody';
import {
  useUserRole,
  useGmailConnectionStatus,
  useAppShell,
  useHeroScrolled,
  useOwnAvatar,
} from '@/hooks';
import { useUserStore } from '@/stores/userStore';
import { config } from '@/lib/config';
import { householdService, settingsService } from '@/services';
import { SettingsMainSkeleton } from './SettingsMainSkeleton';
import { SectionHeading } from './SectionHeading';
import type {
  Household,
  Child,
  HouseholdMemberWithUser,
  InboxAddress,
  Subscription,
} from '@/types';
import type { ApprovedSender } from '@/services/settings.service';
import { TIERS } from '@/constants/billing';
import { DEFAULT_HOUSEHOLD_EMOJI } from '@/constants/household';
import { useFeedbackStore } from '@/stores/feedbackStore';

function IconCircle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center w-12 h-12 shrink-0 rounded-full bg-surface-secondary">
      {children}
    </div>
  );
}

const chevron = (
  <ChevronRight
    size={20}
    className="text-muted-foreground"
  />
);

// Figma paints the Settings row labels with `text/headings` (#000000), a
// step darker than the app's body copy — QA reported every row on this
// screen as "#2f2f2f, should be #000000" (YAR-230). ListItem defaults to
// body copy for everyone else, so the darker tone is opted into here.
const ROW_TITLE = 'text-text-headings';

// General contact address (same one used in the Terms of Service). Kept as a
// constant so the Email Support entry below is easy to find and update.
const SUPPORT_EMAIL = 'hello@yaruta.app';
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
  'Yaruta Support',
)}`;

export function SettingsMain() {
  const navigate = useNavigate();
  const { isPrimary } = useUserRole();
  const displayName = useUserStore((s) => s.displayName);
  const ownAvatar = useOwnAvatar();
  const {
    householdId,
    householdResolved,
    onOpenDrawer,
    openHouseholdSwitcher,
  } = useAppShell();
  const [heroRef, isScrolled] = useHeroScrolled<HTMLDivElement>();

  const [loading, setLoading] = useState(true);
  const [household, setHousehold] = useState<Household | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [members, setMembers] = useState<HouseholdMemberWithUser[]>([]);
  const [, setInboxAddress] = useState<InboxAddress | null>(null);
  const [approvedSenders, setApprovedSenders] = useState<ApprovedSender[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  // Only meaningful when the user actually belongs to more than one
  // household — a Switch entry that opens a single-item list is noise.
  const [myHouseholdCount, setMyHouseholdCount] = useState(0);
  // YAR-503: an account may own at most ONE household but join any number.
  // Someone who only joined (Co-Admin, Editor, Viewer) owns none and can
  // still start their own. Read from the membership list, never the active
  // role: an owner viewing someone else's household reads as Editor there.
  // `null` = unknown (the list failed to load) — offer nothing.
  const [ownsHousehold, setOwnsHousehold] = useState<boolean | null>(null);
  // Which legal/help sheet is currently visible. Kept local so the
  // sheets act like transient in-app modals instead of routes.
  const [openSheet, setOpenSheet] = useState<
    'privacy' | 'terms' | 'faq' | null
  >(null);
  // The Send feedback modal is AppShell's (YAR-482): the drawer card opens
  // the same instance, with the same kept draft.
  const openFeedback = useFeedbackStore((s) => s.openFeedback);
  // Walkthrough re-entry (YAR-238 AC 16). PRIMARY-only and only when
  // Gmail is connected — same gating as the Approved Senders row above.
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);

  const loadData = useCallback(async (hid: string) => {
    try {
      const [
        householdData,
        childrenData,
        membersData,
        inboxData,
        sendersData,
        subscriptionData,
        myHouseholds,
      ] = await Promise.all([
        householdService.getHousehold(hid),
        householdService.getChildren(hid),
        householdService.getMembers(hid),
        householdService.getInboxAddress(hid).catch(() => null),
        settingsService.getApprovedSenders(hid),
        householdService.getSubscription(hid).catch(() => null),
        // Non-critical: a failure here just hides the Switch entry rather
        // than failing the whole Settings load.
        householdService.getMyHouseholdsForCurrentUser().catch(() => null),
      ]);

      setHousehold(householdData);
      setChildren(childrenData);
      setMembers(membersData);
      setInboxAddress(inboxData);
      setApprovedSenders(sendersData);
      setSubscription(subscriptionData);
      setMyHouseholdCount(myHouseholds?.length ?? 0);
      // An empty list is not "owns nothing": anyone on this page belongs to
      // the active household, and the lookup returns [] when auth.getUser()
      // fails. Only a non-empty list can confirm the absence of ownership.
      setOwnsHousehold(
        myHouseholds && myHouseholds.length > 0
          ? myHouseholds.some((h) => h.role === 'PRIMARY')
          : null,
      );
    } catch (err) {
      console.error('Failed to load settings data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!householdId) {
      // Only stop showing the skeleton once resolution has FINISHED.
      // Otherwise the first paint (id still pending) renders this page's
      // empty/default state before the real data can possibly arrive.
      if (householdResolved) setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadData(householdId);
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, householdResolved, loadData]);

  const { data: gmailStatus } = useGmailConnectionStatus(householdId);

  type BadgeKind =
    | 'connected'
    | 'connection_issue'
    | 'paused'
    | 'not_connected';

  // YAR-312 C3/C12: a user-initiated pause is not a connection problem —
  // show "Paused" like the nav drawer does, not "Connection issue".
  const badgeKind: BadgeKind = !gmailStatus
    ? 'not_connected'
    : gmailStatus.state === 'connected'
      ? 'connected'
      : gmailStatus.state === 'paused'
        ? 'paused'
        : gmailStatus.state === 'expired' ||
            gmailStatus.state === 'expiring_soon' ||
            gmailStatus.state === 'needs_attention' ||
            // A revoked grant needs Reconnect, the same as the screen it
            // opens (YAR-150) — not "Not connected".
            gmailStatus.state === 'revoked'
          ? 'connection_issue'
          : 'not_connected';

  // Dot colours match the Email Monitoring screen's badge (YAR-150) and the
  // Settings row in Figma: connected #17825a (7708:26482), connection issue
  // #a24613 (7760:47071), not connected #a82222 (7759:46822) — `icon-error`,
  // not `danger` (#c94040). Figma has no paused Settings frame, so Paused
  // takes the screen badge's disabled dot (#999999, 7761:48987).
  const badgeStyles: Record<BadgeKind, { dot: string; label: string }> = {
    connected: { dot: 'bg-success', label: 'Connected' },
    connection_issue: { dot: 'bg-warning', label: 'Connection issue' },
    paused: { dot: 'bg-icon-disabled', label: 'Paused' },
    not_connected: { dot: 'bg-icon-error', label: 'Not connected' },
  };

  const badge = badgeStyles[badgeKind];
  const showSenders = badgeKind !== 'not_connected';

  return (
    <div className="flex flex-col min-h-full bg-surface">
      {/* Fixed compressed header — appears on scroll, mirroring the
          Today/Review/Activity/Schedule tabs. Hidden (and inert) until the
          hero title scrolls away so it never duplicates the large title. */}
      <div
        data-testid="settings-compressed-header"
        className={[
          'fixed top-0 left-0 right-0 z-[100] bg-background transition-all duration-200',
          isScrolled
            ? 'opacity-100 translate-y-0 border-b border-border'
            : 'opacity-0 -translate-y-full pointer-events-none',
        ].join(' ')}
      >
        <div className="flex items-center justify-center px-5 py-2">
          <div className="flex-1 min-w-0">
            {onOpenDrawer && (
              <HamburgerMenuButton
                onClick={onOpenDrawer}
                variant="compact"
              />
            )}
          </div>
          <span className="text-base font-semibold text-text-heading">
            Settings
          </span>
          <div className="flex-1 min-w-0" />
        </div>
      </div>

      <div className="ph-no-capture flex-1 px-4 pb-8 pt-5">
        {/* Hero header — hides on scroll. Carries the hamburger + the large
            "Settings" title (the only always-visible copy). */}
        <div ref={heroRef}>
          {onOpenDrawer && (
            <div className="mb-4">
              <HamburgerMenuButton onClick={onOpenDrawer} />
            </div>
          )}
          <h1 className="text-[40px] font-bold text-text-heading leading-tight mb-6">
            Settings
          </h1>
        </div>

        {loading ? (
          <SettingsMainSkeleton />
        ) : (
          <>
            {/* Account */}
            <SectionHeading>Account</SectionHeading>
            <div className="border border-border rounded-lg overflow-hidden mb-6">
              <ListItem
                icon={
                  <ProfileAvatar
                    imagePath={ownAvatar.imagePath}
                    directUrl={ownAvatar.directUrl}
                    emoji={ownAvatar.emoji}
                    color={ownAvatar.color ?? undefined}
                    size="md"
                  />
                }
                title="Profile"
                subtitle={displayName ?? undefined}
                titleClassName={ROW_TITLE}
                trailing={chevron}
                onClick={() => navigate('/settings/profile')}
                showDivider={isPrimary}
                className="!py-3"
              />
              {/* Billing — only the household owner can change the plan. */}
              {isPrimary && (
                <ListItem
                  icon={
                    <IconCircle>
                      <CreditCard
                        size={22}
                        className="text-icon-default"
                      />
                    </IconCircle>
                  }
                  title="Plan"
                  subtitle={
                    subscription
                      ? TIERS[subscription.tier].label +
                        (subscription.tier === 'FREE' ? ' (Beta)' : '')
                      : 'Free (Beta)'
                  }
                  titleClassName={ROW_TITLE}
                  trailing={chevron}
                  onClick={() => navigate('/settings/plan')}
                  className="!py-3"
                />
              )}
            </div>

            {/* Household */}
            <SectionHeading>Household</SectionHeading>
            <div className="border border-border rounded-lg overflow-hidden mb-6">
              <ListItem
                icon={
                  <ProfileAvatar
                    imagePath={household?.avatar_image_path}
                    emoji={household?.emoji_avatar ?? DEFAULT_HOUSEHOLD_EMOJI}
                    color={household?.emoji_color ?? undefined}
                    size="md"
                  />
                }
                title="Household Profile"
                subtitle={household?.name ?? undefined}
                titleClassName={ROW_TITLE}
                trailing={chevron}
                onClick={() => navigate('/settings/household')}
                showDivider
                className="!py-3"
              />
              <ListItem
                icon={
                  <IconCircle>
                    <User
                      size={22}
                      className="text-icon-default"
                    />
                  </IconCircle>
                }
                title="Children"
                subtitle={`${children.length} ${children.length === 1 ? 'Child' : 'Children'}`}
                titleClassName={ROW_TITLE}
                trailing={chevron}
                onClick={() => navigate('/settings/children')}
                showDivider
                className="!py-3"
              />
              <ListItem
                icon={
                  <IconCircle>
                    <Users
                      size={22}
                      className="text-icon-default"
                    />
                  </IconCircle>
                }
                title="Household Members"
                subtitle={`${members.length} ${members.length === 1 ? 'Member' : 'Members'}`}
                titleClassName={ROW_TITLE}
                trailing={chevron}
                onClick={() => navigate('/settings/members')}
                showDivider={myHouseholdCount > 1 || ownsHousehold === false}
                className="!py-3"
              />
              {/* Switch household — the ONLY way to reach a second
                  household. A user may be invited into others while
                  owning at most one, so without this entry an accepted
                  invitation would be unreachable. Hidden at count <= 1. */}
              {myHouseholdCount > 1 && openHouseholdSwitcher && (
                <ListItem
                  icon={
                    <IconCircle>
                      <ArrowLeftRight
                        size={22}
                        className="text-icon-default"
                      />
                    </IconCircle>
                  }
                  title="Switch Household"
                  subtitle={`${myHouseholdCount} Households`}
                  titleClassName={ROW_TITLE}
                  trailing={chevron}
                  onClick={openHouseholdSwitcher}
                  showDivider={ownsHousehold === false}
                  className="!py-3"
                />
              )}
              {/* YAR-503: someone who only joined other families can start
                  their own — they keep every membership they have. */}
              {ownsHousehold === false && (
                <ListItem
                  icon={
                    <IconCircle>
                      <Plus
                        size={22}
                        className="text-icon-default"
                      />
                    </IconCircle>
                  }
                  title="Create my own household"
                  titleClassName={ROW_TITLE}
                  trailing={chevron}
                  onClick={() => navigate('/create-household')}
                  className="!py-3"
                />
              )}
            </div>

            {/* Email Setup — PRIMARY only */}
            {isPrimary && (
              <>
                <SectionHeading>Email Setup</SectionHeading>
                <div className="border border-border rounded-lg overflow-hidden mb-6">
                  <ListItem
                    icon={
                      <IconCircle>
                        <Mail
                          size={22}
                          className="text-icon-default"
                        />
                      </IconCircle>
                    }
                    title="Email Monitoring"
                    titleClassName={ROW_TITLE}
                    /* The connection state belongs UNDER the row title, in
                       the subtitle slot every other row uses — it used to
                       share the trailing slot with the chevron, which read
                       as a second label sitting beside the title (YAR-230).
                       The dot stays: it is the only colour-coded signal
                       separating "Connection issue" from "Not connected". */
                    subtitle={
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={[
                            'inline-block w-2 h-2 rounded-full shrink-0',
                            badge.dot,
                          ].join(' ')}
                        />
                        {badge.label}
                      </span>
                    }
                    trailing={chevron}
                    onClick={() => navigate('/settings/forwarding')}
                    showDivider={showSenders}
                    className="!py-3"
                  />
                  {showSenders && (
                    <ListItem
                      icon={
                        <IconCircle>
                          <ShieldCheck
                            size={22}
                            className="text-icon-default"
                          />
                        </IconCircle>
                      }
                      title="Approved Senders"
                      subtitle={(() => {
                        // Exclude system_managed rows (e.g. user's own Gmail
                        // seeded on connect) so the count reflects what the
                        // user actually added in onboarding/settings.
                        const userPicked = approvedSenders.filter(
                          (s) => !s.system_managed,
                        ).length;
                        return `${userPicked} ${userPicked === 1 ? 'Sender' : 'Senders'}`;
                      })()}
                      titleClassName={ROW_TITLE}
                      trailing={chevron}
                      onClick={() => navigate('/settings/senders')}
                      showDivider={showSenders}
                      className="!py-3"
                    />
                  )}
                  {showSenders && (
                    <ListItem
                      icon={
                        <IconCircle>
                          <Forward
                            size={22}
                            className="text-icon-default"
                          />
                        </IconCircle>
                      }
                      title="Add an older email"
                      subtitle="Replay the forwarding guide"
                      titleClassName={ROW_TITLE}
                      trailing={chevron}
                      onClick={() => setWalkthroughOpen(true)}
                      className="!py-3"
                    />
                  )}
                </div>
              </>
            )}

            {/* Preferences. The Notifications screen now holds nothing but the
                SMS evening digest, which is PRIMARY-only in beta — so gate the
                whole section rather than route anyone to an empty page. This
                row was the section's only entry. */}
            {config.enableSms && isPrimary && (
              <>
                <SectionHeading>Preferences</SectionHeading>
                <div className="border border-border rounded-lg overflow-hidden mb-6">
                  <ListItem
                    icon={
                      <IconCircle>
                        <Bell
                          size={22}
                          className="text-icon-default"
                        />
                      </IconCircle>
                    }
                    title="Notifications"
                    subtitle="SMS"
                    titleClassName={ROW_TITLE}
                    trailing={chevron}
                    onClick={() => navigate('/settings/notifications')}
                    className="!py-3"
                  />
                </div>
              </>
            )}

            {/* Help & About */}
            <SectionHeading>Help & About</SectionHeading>
            <div className="border border-border rounded-lg overflow-hidden mb-6">
              <ListItem
                icon={
                  <IconCircle>
                    <MessageSquare
                      size={22}
                      className="text-icon-default"
                    />
                  </IconCircle>
                }
                title="Send Feedback"
                titleClassName={ROW_TITLE}
                trailing={chevron}
                showDivider
                className="!py-3"
                onClick={() => openFeedback()}
              />
              <ListItem
                icon={
                  <IconCircle>
                    <HelpCircle
                      size={22}
                      className="text-icon-default"
                    />
                  </IconCircle>
                }
                title="Frequently Asked Questions"
                titleClassName={ROW_TITLE}
                trailing={chevron}
                showDivider
                className="!py-3"
                onClick={() => setOpenSheet('faq')}
              />
              <ListItem
                icon={
                  <IconCircle>
                    <Mail
                      size={22}
                      className="text-icon-default"
                    />
                  </IconCircle>
                }
                title="Email Support"
                titleClassName={ROW_TITLE}
                trailing={chevron}
                showDivider
                className="!py-3"
                onClick={() => {
                  window.location.href = SUPPORT_MAILTO;
                }}
              />
              <ListItem
                icon={
                  <IconCircle>
                    <Shield
                      size={22}
                      className="text-icon-default"
                    />
                  </IconCircle>
                }
                title="Privacy Policy"
                titleClassName={ROW_TITLE}
                trailing={chevron}
                showDivider
                className="!py-3"
                onClick={() => setOpenSheet('privacy')}
              />
              <ListItem
                icon={
                  <IconCircle>
                    <FileText
                      size={22}
                      className="text-icon-default"
                    />
                  </IconCircle>
                }
                title="Terms of Service"
                titleClassName={ROW_TITLE}
                trailing={chevron}
                className="!py-3"
                onClick={() => setOpenSheet('terms')}
              />
            </div>
          </>
        )}
      </div>

      {/* Legal/help overlays — opened from the list items above. Per the
          Yaruta DS v1, these slide in over Settings instead of routing
          to a separate page, so the user never loses their place. */}
      {openSheet === 'privacy' && (
        <FullScreenSheet
          title="Privacy Policy"
          onClose={() => setOpenSheet(null)}
        >
          <PrivacyPolicyBody />
        </FullScreenSheet>
      )}
      {openSheet === 'terms' && (
        <FullScreenSheet
          title="Terms of Service"
          onClose={() => setOpenSheet(null)}
        >
          <TermsOfServiceBody />
        </FullScreenSheet>
      )}
      {openSheet === 'faq' && (
        <FullScreenSheet
          title="Frequently Asked Questions"
          onClose={() => setOpenSheet(null)}
        >
          <FaqBody />
        </FullScreenSheet>
      )}

      {householdId && (
        <AhaWalkthroughModal
          open={walkthroughOpen}
          onClose={() => setWalkthroughOpen(false)}
          householdId={householdId}
          startAtStep={1}
        />
      )}
    </div>
  );
}
